import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Check, X, Layers, Info, Image } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import TirePositionVisualizer from './TirePositionVisualizer';

const TemplateManager = ({ isOpen, onClose, onSelectTemplate, vehicleType = null }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState(vehicleType || 'all');
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
  }, [isOpen, selectedType]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const params = selectedType !== 'all' ? { vehicle_type: selectedType } : {};
      const response = await api.get('/api/tire-templates', { params });
      setTemplates(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
      toast.error('Erro ao carregar templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = (template) => {
    if (onSelectTemplate) {
      onSelectTemplate(template);
      onClose();
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('Deseja realmente excluir este template?')) return;

    try {
      await api.delete(`/api/tire-templates/${templateId}`);
      toast.success('Template excluído com sucesso');
      loadTemplates();
    } catch (error) {
      console.error('Erro ao excluir template:', error);
      toast.error(error.response?.data?.message || 'Erro ao excluir template');
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Layers className="w-5 h-5" />
              Templates de Pneus
            </h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Novo Template
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Tipo de Veículo:
              </label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              >
                <option value="all">Todos</option>
                <option value="car">Carro/Van</option>
                <option value="truck">Caminhão</option>
                <option value="motorcycle">Motocicleta</option>
              </select>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : templates.length === 0 ? (
              <div className="text-center py-12">
                <Layers className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400">
                  Nenhum template encontrado
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {templates.map((template) => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    onSelect={() => handleSelectTemplate(template)}
                    onDelete={() => handleDeleteTemplate(template.id)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>

      {/* Create Template Modal */}
      {showCreateModal && (
        <CreateTemplateModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadTemplates();
          }}
        />
      )}
    </>
  );
};

// Template Card Component
const TemplateCard = ({ template, onSelect, onDelete }) => {
  const [expanded, setExpanded] = useState(false);

  const getVehicleTypeLabel = (type) => {
    const labels = {
      car: 'Carro/Van',
      truck: 'Caminhão',
      motorcycle: 'Motocicleta'
    };
    return labels[type] || type;
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-blue-500 dark:hover:border-blue-400 transition-colors">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-gray-900 dark:text-white">
              {template.name}
            </h3>
            {template.is_default && (
              <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-xs rounded">
                Padrão
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {template.description}
          </p>
        </div>
        {!template.is_default && (
          <button
            onClick={onDelete}
            className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-4 text-sm">
          <span className="text-gray-600 dark:text-gray-400">
            Tipo: <span className="font-medium text-gray-900 dark:text-white">
              {getVehicleTypeLabel(template.vehicle_type)}
            </span>
          </span>
          <span className="text-gray-600 dark:text-gray-400">
            Pneus: <span className="font-medium text-gray-900 dark:text-white">
              {template.total_tires}
            </span>
          </span>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-1"
        >
          <Info className="w-4 h-4" />
          {expanded ? 'Ocultar' : 'Ver'} Posições
        </button>
      </div>

      {/* Positions (expandable) */}
      {expanded && (
        <div className="mb-3 space-y-3">
          {/* Imagem de Referência */}
          {template.reference_image && (
            <div className="rounded border border-gray-200 dark:border-gray-700 overflow-hidden">
              <img
                src={`https://viploclocadora.com.br${template.reference_image}`}
                alt={`Diagrama ${template.name}`}
                className="w-full h-auto object-contain bg-gray-50 dark:bg-gray-900"
              />
            </div>
          )}

          {/* Visualização Gráfica */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Diagrama de Posições
            </h4>
            <TirePositionVisualizer template={template} compact={false} />
          </div>

          {/* Lista de Posições */}
          <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Lista de Posições
            </h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {template.positions.map((pos, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                  <span className="text-gray-700 dark:text-gray-300">{pos.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={onSelect}
          className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm flex items-center justify-center gap-2"
        >
          <Check className="w-4 h-4" />
          Usar Template
        </button>
      </div>
    </div>
  );
};

// Create Template Modal Component
const CreateTemplateModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    vehicle_type: 'truck',
    total_tires: 10,
    is_default: false
  });
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [referenceImage, setReferenceImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Apenas imagens são permitidas');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Imagem deve ter no máximo 5MB');
        return;
      }
      setReferenceImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddPosition = () => {
    setPositions([
      ...positions,
      { position: '', label: '', required: true }
    ]);
  };

  const handleRemovePosition = (index) => {
    setPositions(positions.filter((_, i) => i !== index));
  };

  const handlePositionChange = (index, field, value) => {
    const newPositions = [...positions];
    newPositions[index][field] = value;
    setPositions(newPositions);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (positions.length === 0) {
      toast.error('Adicione pelo menos uma posição de pneu');
      return;
    }

    const invalidPositions = positions.filter(p => !p.position || !p.label);
    if (invalidPositions.length > 0) {
      toast.error('Preencha todos os campos das posições');
      return;
    }

    setLoading(true);
    try {
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('description', formData.description);
      submitData.append('vehicle_type', formData.vehicle_type);
      submitData.append('total_tires', positions.length);
      submitData.append('positions', JSON.stringify(positions));
      submitData.append('is_default', formData.is_default);

      if (referenceImage) {
        submitData.append('reference_image', referenceImage);
      }

      await api.post('/api/tire-templates', submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Template criado com sucesso');
      onSuccess();
    } catch (error) {
      console.error('Erro ao criar template:', error);
      toast.error(error.response?.data?.message || 'Erro ao criar template');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            Criar Novo Template
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Nome *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Ex: Caminhão Toco 6x2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Tipo de Veículo *
              </label>
              <select
                required
                value={formData.vehicle_type}
                onChange={(e) => setFormData({ ...formData, vehicle_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="car">Carro/Van</option>
                <option value="truck">Caminhão</option>
                <option value="motorcycle">Motocicleta</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Descrição
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Descrição do template"
            />
          </div>

          {/* Imagem de Referência */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Imagem de Referência (Diagrama)
            </label>
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                  id="reference-image-input"
                />
                <label
                  htmlFor="reference-image-input"
                  className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:border-blue-500 dark:hover:border-blue-400 transition-colors"
                >
                  <Image className="w-5 h-5 text-gray-400" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {referenceImage ? referenceImage.name : 'Clique para selecionar imagem'}
                  </span>
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  PNG, JPG ou GIF (máx. 5MB)
                </p>
              </div>
              {imagePreview && (
                <div className="w-32 h-32 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full h-full object-contain bg-gray-50 dark:bg-gray-900"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_default"
              checked={formData.is_default}
              onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="is_default" className="text-sm text-gray-700 dark:text-gray-300">
              Definir como template padrão para este tipo de veículo
            </label>
          </div>

          {/* Positions */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Posições de Pneus ({positions.length})
              </label>
              <button
                type="button"
                onClick={handleAddPosition}
                className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Adicionar Posição
              </button>
            </div>

            <div className="space-y-2 max-h-64 overflow-y-auto">
              {positions.map((pos, index) => (
                <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400 w-8">
                    #{index + 1}
                  </span>
                  <input
                    type="text"
                    required
                    value={pos.position}
                    onChange={(e) => handlePositionChange(index, 'position', e.target.value)}
                    className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    placeholder="ID (ex: front_left)"
                  />
                  <input
                    type="text"
                    required
                    value={pos.label}
                    onChange={(e) => handlePositionChange(index, 'label', e.target.value)}
                    className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    placeholder="Label (ex: Dianteiro Esquerdo)"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemovePosition(index)}
                    className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}

              {positions.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                  Nenhuma posição adicionada. Clique em "Adicionar Posição" para começar.
                </p>
              )}
            </div>
          </div>
        </form>

        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || positions.length === 0}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Criando...' : 'Criar Template'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateManager;
